import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const PAGE_SIZE = 20;
const formatDate = value => new Date(value).toLocaleString("it-IT", {
  day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
});

export default function ProductionChatLots({ cards, selected, sending, onOpen, search, filterKey, filtered }) {
  const [expandedLot, setExpandedLot] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [collapsedSearchLots, setCollapsedSearchLots] = useState([]);
  useEffect(() => { setVisibleCount(PAGE_SIZE); setCollapsedSearchLots([]); }, [search, filterKey]);
  const lots = useMemo(() => {
    const grouped = new Map();
    // Cards arrive ordered by latest activity, so insertion order also orders the lots.
    cards.forEach(card => {
      if (!grouped.has(card.lotto)) grouped.set(card.lotto, {
        lotto: card.lotto, cards: [], unreadCount: 0, mentionCount: 0, latestMessageAt: card.latestMessageAt
      });
      const lot = grouped.get(card.lotto);
      lot.cards.push(card);
      lot.unreadCount += card.unreadCount || 0;
      lot.mentionCount += card.mentionCount || 0;
    });
    return [...grouped.values()];
  }, [cards]);

  return <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
      {lots.length} lotti · {cards.length} OC{filtered || search.trim() ? " corrispondenti ai filtri" : ""}
    </Typography>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: selected ? "1fr" : "repeat(auto-fill,minmax(280px,1fr))" },
      gap: 1.5, maxHeight: selected ? { lg: "calc(100vh - 340px)" } : undefined, overflowY: "auto" }}>
      {lots.slice(0, visibleCount).map((lot, index) => {
        const expanded = search.trim() ? !collapsedSearchLots.includes(lot.lotto) : expandedLot === lot.lotto;
        const panelId = `production-lot-panel-${index}`;
        return <Box key={lot.lotto} sx={{ gridColumn: expanded ? "1 / -1" : undefined, alignSelf: "start",
          border: "1px solid", borderColor: expanded ? "primary.main" : "#e5e7eb", borderRadius: 2, bgcolor: "#fff", overflow: "hidden" }}>
          <Button fullWidth aria-expanded={expanded} aria-controls={expanded ? panelId : undefined}
            onClick={() => search.trim() ? setCollapsedSearchLots(current => current.includes(lot.lotto)
              ? current.filter(value => value !== lot.lotto) : [...current, lot.lotto])
              : setExpandedLot(current => current === lot.lotto ? null : lot.lotto)}
            sx={{ p: 2, textAlign: "left", textTransform: "none", color: "text.primary", justifyContent: "space-between", gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>Lotto {lot.lotto}</Typography>
              <Typography variant="body2" color="text.secondary">{lot.cards.length} OC</Typography>
              <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
                {lot.unreadCount > 0 && <Chip component="span" label={`${lot.unreadCount} non letti`} color="primary" size="small" />}
                {lot.mentionCount > 0 && <Chip component="span" label={`${lot.mentionCount} menzioni a me`} color="warning" size="small" />}
              </Stack>
              {lot.latestMessageAt && <Typography variant="caption" color="text.secondary">Ultima attività {formatDate(lot.latestMessageAt)}</Typography>}
            </Box>
            <KeyboardArrowDownIcon sx={{ transform: expanded ? "rotate(180deg)" : undefined }} />
          </Button>
          {expanded && <Box id={panelId} role="region" aria-label={`OC del lotto ${lot.lotto}`} sx={{ p: 1.5, pt: 0,
            display: "grid", gridTemplateColumns: { xs: "1fr", sm: selected ? "1fr" : "repeat(auto-fill,minmax(260px,1fr))" }, gap: 1.5 }}>
            {lot.cards.map(card => <Button key={card.key} onClick={() => onOpen(card)} disabled={sending}
              aria-pressed={selected?.key === card.key}
              sx={{ display: "block", textAlign: "left", textTransform: "none", p: 2, color: "text.primary", bgcolor: "#fff",
                border: selected?.key === card.key ? "2px solid #1f6feb" : "1px solid #e5e7eb", borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography fontWeight={800}>{card.title}</Typography>
                {card.unreadCount > 0 && <Chip component="span" label={`${card.unreadCount} non letti`} color="primary" size="small" />}
              </Stack>
              <Typography variant="caption" color="text.secondary">Lotto {card.lotto}{card.ragioneSocialeCliente ? ` · ${card.ragioneSocialeCliente}` : ""}</Typography>
              {card.rifOrdCli && <Typography variant="body2" color="text.secondary">Rif. cliente: {card.rifOrdCli}</Typography>}
              <Typography variant="body2" color="text.secondary">Seguito da: {card.quotationCreatedByName || card.quotationCreatedBy || "Non registrato"}</Typography>
              {card.mentionCount > 0 && <Chip component="span" label="Ti hanno menzionato" color="warning" size="small" sx={{ mt: 1 }} />}
              <Typography variant="body2" sx={{ mt: 1, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflowWrap: "anywhere" }}>
                {card.latestBody ? `${card.latestSenderName || "Operatore"}: ${card.latestBody}` : "Nessun messaggio · Apri chat OC"}
              </Typography>
              {card.latestMessageAt && <Typography variant="caption" color="text.secondary">{formatDate(card.latestMessageAt)}</Typography>}
            </Button>)}
          </Box>}
        </Box>;
      })}
    </Box>
    {visibleCount < lots.length && <Button fullWidth variant="outlined" sx={{ mt: 2 }}
      onClick={() => setVisibleCount(current => current + PAGE_SIZE)}>Carica altri lotti ({lots.length - visibleCount})</Button>}
  </Box>;
}
